import { createRouter, createWebHistory } from 'vue-router'
import { waitForAuthInit, authSession } from './composables/useAuth'

// Preload Dashboard component since it's the most commonly accessed view
// This runs in the background after initial page load
const DashboardComponent = () => import('./views/Dashboard.vue')

const routes = [
  {
    path: '/',
    name: 'landing',
    component: () => import('./views/Landing.vue')
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardComponent,
    meta: { requiresAuth: true }
  },
  {
    path: '/memo/:id',
    name: 'memo-detail',
    component: () => import('./views/MemoDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('./views/Settings.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/statistics',
    name: 'statistics',
    component: () => import('./views/Statistics.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('./views/Privacy.vue')
  },
  {
    path: '/impressum',
    name: 'impressum',
    component: () => import('./views/Impressum.vue')
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('./views/Support.vue')
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('./views/Admin.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/subscription',
    name: 'subscription',
    component: () => import('./views/Subscription.vue'),
    meta: { requiresAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Auth guard - optimized for speed
router.beforeEach(async (to, _from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Fast path: if auth is already known, don't wait
  // Only wait for auth init on routes that require auth or landing page redirect
  if (requiresAuth || to.name === 'landing') {
    // Wait for auth to initialize (handles OAuth callbacks)
    // This is necessary for protected routes and landing page redirect logic
    await waitForAuthInit()
  }

  // If user is signed in and going to landing page, redirect to dashboard
  if (to.name === 'landing' && authSession.value) {
    next({ name: 'dashboard' })
    return
  }

  if (requiresAuth) {
    if (!authSession.value) {
      // Not authenticated, redirect to landing page
      next({ name: 'landing' })
    } else {
      next()
    }
  } else {
    next()
  }
})

// Prefetch commonly accessed components after router is ready
router.isReady().then(() => {
  // Prefetch Dashboard on idle if not already loaded
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      DashboardComponent()
    })
  }
})

export default router
